class ProductGallery extends HTMLElement {
	connectedCallback() {
		// Include Shopify, Sanity, and Mux slides. Only Shopify figures carry a
		// media id, but every rendered slide has a position and the dots index the
		// complete ordered gallery.
		this.figures = Array.from(this.querySelectorAll('figure[data-position]'));
		if (this.figures.length === 0) return;
		this.figures.forEach((fig, idx) => fig.toggleAttribute('data-active', idx === 0));
		// Fade lazy slides in as they decode — see the [data-fade] rules in
		// product-gallery.liquid. The attribute is set from script on purpose:
		// a gallery whose JS never runs keeps opaque images instead of blank
		// slides.
		this.setAttribute('data-fade', '');
		this.querySelectorAll('.product__media-btn img').forEach((img) => {
			const reveal = () => img.setAttribute('data-loaded', '');
			// A cached image is already complete and will never fire `load`, so
			// it would wait at opacity 0 for an event that has already passed.
			if (img.complete) return reveal();
			img.addEventListener('load', reveal, { once: true });
			// A broken image must not leave a permanently blank slide either.
			img.addEventListener('error', reveal, { once: true });
		});
		this.addEventListener('variant:change', this.onVariantChange);

		this.wrap = this.closest('.product__gallery-wrap');
		this.dialog = this.wrap?.querySelector('.product__lightbox');
		this.dots = Array.from(this.wrap?.querySelectorAll('.product__dot') || []);
		this.addEventListener('click', this.onClick);
		this.dots.forEach((d) =>
			d.addEventListener('click', () => this.scrollTo({
				left: this.slideLeft(+d.dataset.dot), behavior: 'smooth',
			})));

		// The PDP uses native scroll snap, so derive the selected dot from the slide
		// nearest the viewport instead of relying on Embla transform events.
		this.slides = Array.from(this.querySelectorAll('.product__slide'));
		this.setActiveDot = (i) => this.dots.forEach((d, idx) => d.toggleAttribute('data-active', idx === i));
		// Warm slide 1 before the first swipe, then keep one slide of headroom
		// ahead of the visitor as they scroll.
		this.activeIndex = 0;
		this.warmNeighbours(0);
		if (this.dots.length) {
			this.setActiveDot(0);
			this.addEventListener('scroll', this.onScroll, { passive: true });
		}
		// The track loops: a clone of the first slide sits after the last one, so
		// swiping forward at the end scrolls into it like any other slide and the
		// script silently returns to the real first slide once the scroll settles.
		this.track = this.querySelector('.product__gallery-track');
		this.buildTailClone();
		// A backward swipe at slide 0 needs the mirror of that — a clone of the
		// last slide before the first — but building it up front would shift the
		// whole track right, and the gallery would open on the wrong photo in the
		// window between first paint and this deferred script. So it is built at
		// the moment the visitor starts a gesture there, when compensating the
		// scroll position is invisible.
		this.addEventListener('touchstart', this.onGestureStart, { passive: true });
		// `scrollend` is what tells us a snap has settled; where it is missing,
		// a quiet period after the last scroll event stands in for it.
		if ('onscrollend' in this) this.addEventListener('scrollend', this.onScrollEnd);
		else this.addEventListener('scroll', this.onScrollIdle, { passive: true });
		// Click-drag for pointers that have no swipe. The PDP deliberately opts
		// out of Embla (see sylvie-carousel.js) in favour of native scroll snap,
		// which WebKit handles more reliably — but Embla is also what gives every
		// other carousel its mouse drag. Without this, a narrow desktop window
		// gets a slider that only the dots can move.
		this.addEventListener('pointerdown', this.onPointerDown);
		this.addEventListener('pointermove', this.onPointerMove);
		this.addEventListener('pointerup', this.onPointerEnd);
		this.addEventListener('pointercancel', this.onPointerEnd);
		// Dragging across a photo would otherwise start a native image drag and
		// leave the gesture half-finished.
		this.addEventListener('dragstart', this.onDragStart);
		// Lightbox: counter + arrows + thumbnail strip + arrow-key navigation,
		// ported from the app's Gallery.svelte (Escape close is native <dialog>).
		this.stageImg = this.dialog?.querySelector('[data-lightbox-img]');
		// Incremented per navigation so a decode that finishes after the user
		// has already moved on can be recognised as stale and dropped.
		this.stageToken = 0;
		this.stage = this.dialog?.querySelector('.product__lightbox-stage');
		this.counter = this.dialog?.querySelector('[data-lightbox-counter]');
		this.thumbs = Array.from(this.dialog?.querySelectorAll('[data-lightbox-thumb]') || []);
		// Only gate the stage behind the fade CSS when showSlide() can actually
		// run (it no-ops with zero thumbs — see the single-slide fallback in
		// product-gallery.liquid). Setting this unconditionally left the stage
		// image at opacity 0 forever on those products, since nothing would ever
		// set [data-loaded] to reveal it.
		if (this.thumbs.length) this.dialog?.setAttribute('data-fade', '');
		this.lightboxIndex = 0;
		this.dialog?.querySelector('.product__lightbox-close')
			?.addEventListener('click', () => this.dialog.close());
		// Otherwise the previous product's blur is still behind the stage the
		// next time the lightbox opens on a different slide.
		this.dialog?.addEventListener('close', () => {
			if (this.stage) this.stage.style.backgroundImage = '';
		});
		this.dialog?.addEventListener('click', (e) => {
			if (e.target === this.dialog) { this.dialog.close(); return; }
			if (e.target.closest('[data-lightbox-prev]')) { this.showSlide(this.lightboxIndex - 1); return; }
			if (e.target.closest('[data-lightbox-next]')) { this.showSlide(this.lightboxIndex + 1); return; }
			const thumb = e.target.closest('[data-lightbox-thumb]');
			if (thumb) this.showSlide(+thumb.dataset.lightboxThumb);
		});
		this.dialog?.addEventListener('keydown', (e) => {
			if (e.key === 'ArrowLeft') this.showSlide(this.lightboxIndex - 1);
			if (e.key === 'ArrowRight') this.showSlide(this.lightboxIndex + 1);
		});
	}

	// Start the neighbouring slides' photos before the visitor swipes to them,
	// so the fade has usually already run by the time the slide lands.
	// Deliberately low priority: warming must never compete with the media
	// currently on screen.
	warmNeighbours(index) {
		[index - 1, index + 1].forEach((i) => {
			const slide = this.slides?.[i];
			if (!slide) return;
			slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
				// Order matters — assigning `loading` starts the fetch, so the
				// priority has to be in place first.
				img.fetchPriority = 'low';
				img.loading = 'eager';
			});
		});
	}

	// Travel beyond which a pointer gesture counts as a drag rather than a tap.
	// Below it the click must still open the lightbox.
	static DRAG_SLOP_PX = 5;

	onPointerDown = (event) => {
		// Touch already scrolls natively, and hijacking it would fight the
		// browser's own snap and momentum.
		if (event.pointerType === 'touch' || event.button !== 0) return;
		// Desktop renders the slides as a grid with nothing to scroll.
		if (this.scrollWidth <= this.clientWidth) return;
		// A mouse drag loops like a swipe does, so the backward clone has to be
		// in place before the start position is captured — building it shifts
		// the scroll offset the drag is measured against.
		this.onGestureStart();
		this.dragStartX = event.clientX;
		this.dragStartScroll = this.scrollLeft;
		this.dragDistance = 0;
		this.suppressClick = false;
		// `scroll-snap-type: x mandatory` re-snaps after every scrollLeft
		// assignment, so a drag would jump between slides instead of following
		// the cursor. Suspending snap for the gesture and restoring it on release
		// is what makes the release settle onto the nearest slide.
		this.style.scrollSnapType = 'none';
		this.classList.add('is-dragging');
		try { this.setPointerCapture(event.pointerId); } catch (error) {}
	};

	onPointerMove = (event) => {
		if (this.dragStartX == null) return;
		const deltaX = event.clientX - this.dragStartX;
		this.dragDistance = Math.max(this.dragDistance, Math.abs(deltaX));
		this.scrollLeft = this.dragStartScroll - deltaX;
		event.preventDefault();
	};

	onPointerEnd = (event) => {
		if (this.dragStartX == null) return;
		this.dragStartX = null;
		this.classList.remove('is-dragging');
		try { this.releasePointerCapture(event.pointerId); } catch (error) {}
		// Restoring snap lets the browser settle on the nearest slide by itself.
		this.style.scrollSnapType = '';
		// The click fires after pointerup, so a real drag would also open the
		// lightbox. Swallow exactly that one click.
		if (this.dragDistance > ProductGallery.DRAG_SLOP_PX) this.suppressClick = true;
	};

	onDragStart = (event) => {
		if (this.dragStartX != null) event.preventDefault();
	};

	onScroll = () => {
		if (this.scrollFrame) return;
		this.scrollFrame = requestAnimationFrame(() => {
			this.scrollFrame = 0;
			// Every slide is exactly one viewport wide, so the offset from the
			// first real slide gives the index directly — and wrapping it is what
			// keeps the dots honest over the clones, which sit at -1 and N.
			const width = this.clientWidth || 1;
			const count = this.slides.length;
			const raw = Math.round((this.scrollLeft - this.slideLeft(0)) / width);
			const index = ((raw % count) + count) % count;
			if (index !== this.activeIndex) {
				this.activeIndex = index;
				this.warmNeighbours(index);
			}
			this.setActiveDot(index);
		});
	};

	// Where a real slide starts. Read from layout rather than multiplied out of
	// clientWidth, because a leading clone shifts every slide right by one.
	slideLeft(index) {
		return this.slides[index]?.offsetLeft ?? 0;
	}

	// A clone stands in for a real slide for the length of one swipe, so it must
	// look identical without behaving like one: no `data-position` (the figure
	// list drives variant selection), and no second player of any kind, which
	// would mean a second video loading for a slide the visitor sees for a
	// moment. The poster is the frame it shows at rest anyway.
	buildClone(slide) {
		const clone = slide.cloneNode(true);
		clone.classList.add('product__slide--clone');
		clone.setAttribute('data-clone', '');
		clone.setAttribute('aria-hidden', 'true');
		clone.querySelectorAll('figure').forEach((fig) => {
			fig.removeAttribute('data-position');
			fig.removeAttribute('data-media-id');
			fig.removeAttribute('data-active');
		});
		clone.querySelectorAll('mux-player').forEach((player) => {
			const poster = player.getAttribute('poster');
			const still = document.createElement('div');
			// Same class, so the box keeps the player's size and aspect ratio;
			// the poster is only a background there, so it needs the sizing the
			// video element used to provide for itself.
			still.className = player.className;
			still.style.cssText = player.style.cssText;
			if (poster) still.style.backgroundImage = `url(${poster})`;
			still.style.backgroundSize = 'cover';
			still.style.backgroundPosition = 'center';
			player.replaceWith(still);
		});
		/* The same rule for Vimeo, which is what these slides are since the
		   migration -- the mux-player branch above stopped matching anything
		   and the iframe was being cloned wholesale. Removing it is enough:
		   the poster is painted on the .product__media-btn behind it, which is
		   already what fills the slide until the real player loads.

		   This is not free to leave in. A display:none iframe still fetches
		   its src, so the desktop-hidden clone was loading an entire
		   cross-origin player.vimeo.com document on every PDP with a lead
		   video -- measurable as the lead video id being fetched twice. */
		clone.querySelectorAll('iframe').forEach((frame) => frame.remove());
		// The photo is already decoded in the slide this was copied from, so the
		// clone must not sit behind the lazy-fade waiting for a `load` that will
		// never fire on a cached image.
		clone.querySelectorAll('img').forEach((img) => {
			img.loading = 'eager';
			img.setAttribute('data-loaded', '');
		});
		return clone;
	}

	buildTailClone() {
		if (!this.track || this.slides.length < 2 || this.tailClone) return;
		this.tailClone = this.buildClone(this.slides[0]);
		this.track.append(this.tailClone);
	}

	// Only worth its cost once the visitor is actually at slide 0 and about to
	// pull backwards; inserting it moves the real slides one width to the right,
	// which has to be undone in the same breath or the gallery jumps a photo.
	buildHeadClone() {
		if (!this.track || this.slides.length < 2 || this.headClone) return;
		// Where the visitor is looking, measured before the insertion moves it.
		// The correction is an absolute assignment rather than `+=` on purpose:
		// Chrome already shifts scrollLeft itself to preserve the visual
		// position when content is prepended to a scroller, and adding to that
		// would land a whole slide further along.
		const before = this.scrollLeft;
		this.headClone = this.buildClone(this.slides[this.slides.length - 1]);
		this.track.prepend(this.headClone);
		this.scrollLeft = before + this.headClone.offsetWidth;
	}

	onGestureStart = () => {
		// Not a scroller: single-media products, and desktop, where the grid
		// layout sets overflow back to visible.
		if (this.scrollWidth <= this.clientWidth) return;
		if (this.scrollLeft <= this.slideLeft(0) + 1) this.buildHeadClone();
	};

	// Landing on a clone is the loop's seam: swap in the real slide it copies.
	// The jump is a plain assignment to an exact snap position, so there is no
	// animation to see and mandatory snap has nothing to re-settle.
	settleLoop() {
		if (this.scrollWidth <= this.clientWidth) return;
		// A mouse drag holds the track at an arbitrary offset with snap
		// suspended; pausing mid-drag must not be read as the scroll settling.
		if (this.dragStartX != null) return;
		const last = this.slides.length - 1;
		if (this.tailClone && Math.abs(this.scrollLeft - this.tailClone.offsetLeft) < 2) {
			this.scrollLeft = this.slideLeft(0);
		} else if (this.headClone && Math.abs(this.scrollLeft - this.headClone.offsetLeft) < 2) {
			this.scrollLeft = this.slideLeft(last);
		}
	}

	onScrollEnd = () => this.settleLoop();

	// Stand-in for `scrollend`: momentum scrolling fires a steady stream of
	// scroll events, so a gap in that stream is the gesture coming to rest.
	static SETTLE_MS = 120;

	onScrollIdle = () => {
		clearTimeout(this.settleTimer);
		this.settleTimer = setTimeout(() => this.settleLoop(), ProductGallery.SETTLE_MS);
	};

	disconnectedCallback() {
		this.removeEventListener('scroll', this.onScroll);
		this.removeEventListener('scroll', this.onScrollIdle);
		this.removeEventListener('scrollend', this.onScrollEnd);
		this.removeEventListener('touchstart', this.onGestureStart);
		this.removeEventListener('pointerdown', this.onPointerDown);
		this.removeEventListener('pointermove', this.onPointerMove);
		this.removeEventListener('pointerup', this.onPointerEnd);
		this.removeEventListener('pointercancel', this.onPointerEnd);
		this.removeEventListener('dragstart', this.onDragStart);
		if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
		clearTimeout(this.settleTimer);
	}

	// Must match the transition duration on [data-lightbox-img] in
	// product-gallery.liquid — the pixels are switched while the element is
	// transparent, so the swap is never visible as a cut.
	static STAGE_FADE_MS = 160;

	// Assigning .src directly emptied the stage until the full-size file
	// landed. Decode off-DOM first: the outgoing image holds the frame for the
	// wait, and the incoming one is already painted when it fades in.
	swapStage(url, alt) {
		if (!this.stageImg || !url) return;
		const token = ++this.stageToken;
		const next = new Image();
		next.src = url;
		// Under reduced motion the fade itself is already off (see the
		// [data-fade] CSS), so the STAGE_FADE_MS delay would just be a pointless
		// wait with nothing to hide it — swap as soon as the image decodes.
		const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
		const show = () => {
			if (token !== this.stageToken) return;   // a later navigation won
			this.stageImg.removeAttribute('data-loaded');
			const swap = () => {
				if (token !== this.stageToken) return;
				this.stageImg.src = url;
				this.stageImg.alt = alt;
				this.stageImg.hidden = false;
				// One frame of gap so the browser treats this as a transition
				// from 0 rather than collapsing both changes into one style
				// recalculation.
				requestAnimationFrame(() => this.stageImg.setAttribute('data-loaded', ''));
			};
			if (reducedMotion) swap();
			else setTimeout(swap, ProductGallery.STAGE_FADE_MS);
		};
		// `decode()` rejects on a broken image; show it either way rather than
		// leaving the stage stuck on the previous photo.
		(next.decode ? next.decode() : Promise.resolve()).then(show, show);
	}

	showSlide(index) {
		if (!this.stageImg || this.thumbs.length === 0) return;
		// Loop past the ends, like the app's loop:true Embla modal.
		const count = this.thumbs.length;
		this.lightboxIndex = ((index % count) + count) % count;
		const source = this.thumbs[this.lightboxIndex];
		if (this.counter) this.counter.textContent = `${this.lightboxIndex + 1} / ${count}`;
		this.swapStage(source.dataset.full, source.dataset.alt || '');
		// Refresh the blur placeholder to the slide being navigated TO — it was
		// previously only ever seeded once, from the slide that opened the
		// lightbox, so paging further left the wrong photo's blur behind the
		// stage. Leave the existing background if this slide has none rather
		// than clearing it to blank.
		const slideBg = this.slides?.[this.lightboxIndex]
			?.querySelector('.product__media-btn')?.style.backgroundImage;
		if (slideBg && this.stage) this.stage.style.backgroundImage = slideBg;
		this.thumbs.forEach((t, i) => t.toggleAttribute('data-active', i === this.lightboxIndex));
		source.scrollIntoView?.({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
	}

	onVariantChange = (event) => {
		const mediaId = event.detail?.featuredMediaId;
		if (!mediaId) return;
		const target = this.figures.findIndex((f) => f.dataset.mediaId === String(mediaId));
		if (target < 0) return;
		this.figures.forEach((fig, idx) => fig.toggleAttribute('data-active', idx === target));
		this.scrollTo({ left: this.slideLeft(target), behavior: 'smooth' });
	};

	onClick = (event) => {
		// A drag that ended on a slide must not also open the lightbox.
		if (this.suppressClick) {
			this.suppressClick = false;
			return;
		}
		const btn = event.target.closest('[data-lightbox-open]');
		if (!btn || !this.dialog) return;
		// The placeholder the visitor was already looking at fills the stage
		// while the full-size file decodes, so the first open is never empty.
		if (this.stage) this.stage.style.backgroundImage = btn.style.backgroundImage || '';
		this.showSlide(+btn.dataset.lightboxOpen);
		this.dialog.showModal();
	};
}
customElements.define('product-gallery', ProductGallery);
